// ABOUTME: SPEC-82 Rev 3 (bug 2) — a pre-tap guest request is the tap: release the held welcome exactly as the CTA does, let the
// ABOUTME: short welcome play out, THEN send the request (owner 2026-10-06: no barge-in, the welcome is never cut off).

import { useCallback, useEffect, useRef } from 'react';
import { EVENT, INITIAL, PHASE, nextRelease, queueSlot, releases } from './implicitRelease';
import useHeldRequests from './useHeldRequests';

// Liveness backstops, not the gate (the gate is the welcome's own signals): a release whose welcome never starts (the backend
// already welcomed this page, a dropped socket) must not swallow the guest's request, and neither may a welcome whose closing
// boundary never arrives. Measured locally 2026-10-06: intro_finished -> first welcome audio ~2 s; a brief welcome speaks ~6-10 s.
export const WELCOME_START_CEILING_MS = 10000;
export const WELCOME_TOTAL_CEILING_MS = 30000;

/**
 * @param {object} p
 * @param {object} p.introX useIntroExperiment()'s return (isHolding, tap)
 * @param {boolean} p.agentSpeaking App's `isAgentSpeaking` (true from the first audible chunk until the playout queue drains)
 * @param {Function} [p.isSocketOpen] whether the agent socket is open right now; the held requests wait for it (review L1)
 * @returns {{ intercept: (action: string, send: Function) => boolean, noteTurnBoundary: () => void }}
 *   `intercept` returns true when it took the request (held until the welcome is over); false = send it now, as before.
 */
export default function useImplicitRelease({ introX, agentSpeaking, isSocketOpen }) {
  const stateRef = useRef(INITIAL);
  const ceilingsRef = useRef([]);
  const introRef = useRef(introX);
  introRef.current = introX;
  const held = useHeldRequests({ isSocketOpen });

  const dispatch = useCallback((event) => {
    const prev = stateRef.current;
    stateRef.current = nextRelease(prev, event);
    if (prev.phase !== PHASE.WAITING || stateRef.current.phase !== PHASE.DONE) return;
    ceilingsRef.current.forEach(clearTimeout);
    ceilingsRef.current = [];
    held.deliver();
  }, [held]);

  const armCeilings = useCallback(() => {
    const startCheck = setTimeout(() => { if (!stateRef.current.spoke) dispatch(EVENT.CEILING); }, WELCOME_START_CEILING_MS);
    const total = setTimeout(() => dispatch(EVENT.CEILING), WELCOME_TOTAL_CEILING_MS);
    ceilingsRef.current = [startCheck, total];
  }, [dispatch]);

  // Owner 2026-10-07: a SCREEN MOVE (Vélos/Vesper, a chapter, a variant) moves the screen AT ONCE while the welcome keeps speaking.
  // `send(true)` is the silent form: `agent_speaking: true` is the backend's own contract (manual_tap_speech_gate) — the view moves, the
  // spoken beat is withheld, the welcome is not cut into. Every other releasing action (a cart / wishlist add) still waits for the welcome.
  const place = useCallback((action, send) => {
    if (queueSlot(action) === 'screen') held.sendNow(() => send(true));
    else held.hold(null, send);
  }, [held]);

  const intercept = useCallback((action, send) => {
    const { phase } = stateRef.current;
    if (phase === PHASE.DONE || !releases(action)) return false;
    if (phase === PHASE.WAITING) { place(action, send); return true; }
    if (!introRef.current?.isHolding?.()) return false;
    dispatch(EVENT.RELEASE);
    armCeilings();
    console.log(`[SPEC-82][TAP] implicit release action=${action}`);
    introRef.current.tap(); // exactly the CTA's path: gate down, visit record, intro_finished through the one funnel
    place(action, send);    // AFTER the tap: intro_finished reaches the backend before the request
    return true;
  }, [dispatch, armCeilings, place]);

  const noteTurnBoundary = useCallback(() => dispatch(EVENT.BOUNDARY), [dispatch]);

  useEffect(() => { dispatch(agentSpeaking ? EVENT.SPEAKING : EVENT.SILENT); }, [agentSpeaking, dispatch]);
  useEffect(() => () => ceilingsRef.current.forEach(clearTimeout), []);

  return { intercept, noteTurnBoundary };
}
